import React, { useState, useContext, useMemo, memo, useEffect, useRef, useCallback } from "react";
import { NavLink, Link } from "react-router-dom";
import { SignedIn, SignedOut, SignInButton, UserButton, GoogleOneTap } from "@clerk/clerk-react";

import { GlobalContext } from "../context/GlobalContext";
import { translations } from "../locales/translations_header_footer";
import ToggleMenu from "./ToggleMenu";
import TeacherMessagesBell from "./TeacherMessagesBell";

const ThemeToggle = memo(({ theme, toggleTheme, label = 'Toggle theme' }) => (
  <button onClick={toggleTheme} className="theme-toggle" aria-label={label} title={label}>
    {theme === 'dark' ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
    )}
  </button>
));

const ProfileButton = ({ isOnline, onlineLabel }) => (
  <div
    className={`flex items-center justify-center rounded-full ${isOnline ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-[var(--bg-topbar)]' : ''}`}
    title={isOnline ? onlineLabel : undefined}
  >
    <UserButton mode="modal" />
    {isOnline && <span className="sr-only">{onlineLabel}</span>}
  </div>
);

const SignInIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
    />
  </svg>
);

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', short: 'FR', flag: '🇫🇷' },
  { code: 'ar', label: 'العربية', short: 'AR', flag: '🇲🇦' },
];

const Header = ({ buttons }) => {
  const { language, handleLanguageChange, serverStatus, theme, toggleTheme } = useContext(GlobalContext);
  const t = useMemo(() => translations[language || "fr"].header, [language]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const isOnline = serverStatus === 'online';

  const Span = ({ text }) => (
    <span className="absolute left-1/2 top-full mt-2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap">
      {text}
    </span>
  );

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const handleLanguageSelectionAndCloseMenu = useCallback((lang) => {
    handleLanguageChange(lang);
    setIsMenuOpen(false);
  }, [handleLanguageChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <header dir={language === 'ar' ? 'rtl' : 'ltr'} className="sticky top-0 z-30 w-full ost-topbar flex items-center justify-between py-3 px-3 sm:px-4">
      {/* Small Screen Layout */}
      <div className="nav-break:hidden flex-1 flex justify-start items-center">
        {/* Hamburger Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={toggleMenu}
            className={`p-2 rounded-xl text-[var(--text-main)] hover:bg-[var(--bg-subtle)] focus:outline-none transition ${language === 'ar' ? "-scale-x-100" : ''}`}
            aria-label={t.toggleMenu}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            )}
          </button>
          {isMenuOpen && (
            <div className="absolute start-0 mt-2 w-64 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl shadow-2xl z-40 p-2 animate-in fade-in duration-150">
              <nav className="flex flex-col gap-1 p-1">
                {buttons.map((button, index) => (
                  <NavLink
                    key={index}
                    to={button.to}
                    className={({ isActive }) =>
                      `block px-3.5 py-2.5 text-sm font-semibold rounded-xl transition ${
                        isActive
                          ? 'bg-[var(--brand-50)] text-[var(--brand-700)] dark:text-orange-300 font-extrabold shadow-xs'
                          : 'text-[var(--text-muted)] hover:text-[var(--text-heading)] hover:bg-[var(--bg-subtle)]'
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t[button.label]}
                  </NavLink>
                ))}
              </nav>
              <div className="border-t border-[var(--border-subtle)] mt-2 pt-2 flex flex-col gap-1">
                {LANGUAGES.map((item) => (
                  <button
                    key={item.code}
                    onClick={() => handleLanguageSelectionAndCloseMenu(item.code)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold transition ${
                      language === item.code
                        ? "bg-[var(--brand-50)] text-[var(--brand-700)] dark:text-orange-300 font-extrabold shadow-xs"
                        : "text-[var(--text-muted)] hover:bg-[var(--bg-subtle)]"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-base leading-none">{item.flag}</span>
                      <span>{item.label}</span>
                    </span>
                    {language === item.code && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="nav-break:hidden flex-auto text-center px-1">
        <Link
          to={"/"}
          className="inline-flex items-center gap-1.5 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--text-heading)] transition-colors hover:text-orange-600 truncate"
        >
          {t.title}
        </Link>
      </div>
      <div className="nav-break:hidden flex-1 flex justify-end items-center gap-1.5 sm:gap-2">
        <ThemeToggle theme={theme} toggleTheme={toggleTheme} label={t.toggleTheme} />
        <ToggleMenu language={language} onLanguageChange={handleLanguageChange} compact label={t.selectLanguage} />
        <SignedIn>
          <TeacherMessagesBell label={t.messages} />
          <ProfileButton isOnline={isOnline} onlineLabel={t.serverOnline} />
        </SignedIn>
        <SignedOut>
          <SignInButton mode="modal">
            <button className="ost-btn-primary ost-btn-sm relative group flex items-center gap-1.5 text-xs sm:text-sm px-2.5 py-1.5">
              <SignInIcon />
              <Span text={t.signInButton} />
            </button>
          </SignInButton>
        </SignedOut>
      </div>

      {/* Large Screen Layout */}
      <div className="hidden nav-break:flex flex-1 justify-between items-center">
        <div className="flex items-center gap-4">
          <Link
            to={"/"}
            className="inline-flex items-center gap-2 text-2xl nav-break:text-3xl font-extrabold tracking-tight text-[var(--text-heading)] transition-colors hover:text-orange-600"
          >
            {t.title}
          </Link>
        </div>
        <nav className="flex gap-1 p-1 rounded-[14px] border bg-[var(--bg-subtle)] border-[var(--border-subtle)]">
          {buttons.map((button, index) => (
            <NavLink
              key={index}
              to={button.to}
              className={({ isActive }) =>
                `px-2 py-1.5 text-sm md:px-3 md:py-2 nav-break:text-base lg:px-4 font-semibold rounded-[10px] transition text-[var(--text-muted)] hover:text-[var(--text-heading)] hover:bg-[var(--bg-card)] ${
                  isActive ? 'bg-[var(--bg-card)] text-orange-700 dark:text-orange-400 shadow-sm' : ''
                }`
              }
            >
              {t[button.label]}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle theme={theme} toggleTheme={toggleTheme} label={t.toggleTheme} />
          <ToggleMenu language={language} onLanguageChange={handleLanguageChange} label={t.selectLanguage} />
          <SignedOut>
            <SignInButton mode="modal">
              <button className="ost-btn-primary ost-btn-sm relative group flex items-center gap-2 text-sm nav-break:text-base">
                <SignInIcon />
                <Span text={t.signInButton} />
              </button>
            </SignInButton>
            <GoogleOneTap />
          </SignedOut>
          <SignedIn>
            <TeacherMessagesBell label={t.messages} />
            <ProfileButton isOnline={isOnline} onlineLabel={t.serverOnline} />
          </SignedIn>
        </div>
      </div>
    </header>
  );
};

export default memo(Header);
